/**
 * useQuestions — question + group data hook backed by @v0y4ge/api-client.
 * No seed data in the UI. All data comes through the contract layer.
 * CRUD mutations call the typed client and reconcile local state from the
 * response. A failed write REJECTS and commits nothing (2026-07-25, scrub-audit
 * F6) — the previous fallback-to-a-local-record made failures indistinguishable
 * from successes; callers own the error state.
 */
import { t as i18nT } from "../../lib/i18n";
import { useState, useEffect } from "react";
import {
  listQuestions, createQuestion, patchQuestion, deleteQuestion, duplicateQuestion,
  listQuestionGroups, createQuestionGroup, patchQuestionGroup, deleteQuestionGroup, duplicateQuestionGroup,
} from "@v0y4ge/api-client";
import type { QTextRules } from "./text-validation";
import type { QFileConfig, QInfoConfig, QSelectConfig, QStepperConfig, QYesNoConfig } from "./question-model";
import type { QuestionDto, QuestionGroupDto } from "@v0y4ge/api-contracts";
import { logUpsert, logDelete } from "../../lib/audit";
import { onTenantChange, onTenantSwitching } from "../../shell/access";

/* ── Field-type catalog (static config — not data) ──
 * ONE catalog, read by the editor picker, the library badges + type filter, the
 * group editor, surveys and the event-day renderer. `QFIELD_TYPES` is the FULL
 * set (it must resolve every type that exists in stored data, including retired
 * ones); `Q_PICKER_TYPES` is what an author may choose today. */
export const QFIELD_TYPES = [
  { id: "heading",  labelKey: "questions.useQuestions.sectionHeading", get label() { return i18nT("questions.useQuestions.sectionHeading"); }, icon: "heading", group: "Layout",  opts: false, branch: false, value: false, hint: "Static title / divider." },
  { id: "label",    labelKey: "questions.useQuestions.infoText", get label() { return i18nT("questions.useQuestions.infoText"); },       icon: "text",    group: "Layout",  opts: false, branch: false, value: false, hint: "Guidance text — collects no answer." },
  { id: "short",    labelKey: "questions.useQuestions.shortText", get label() { return i18nT("questions.useQuestions.shortText"); },      icon: "input",   group: "Text",    opts: false, branch: false, value: true,  hint: "Single-line entry." },
  { id: "long",     labelKey: "questions.useQuestions.longText", get label() { return i18nT("questions.useQuestions.longText"); },       icon: "area",    group: "Text",    opts: false, branch: false, value: true,  hint: "Multi-line entry area." },
  { id: "number",   labelKey: "questions.useQuestions.number", get label() { return i18nT("questions.useQuestions.number"); },          icon: "hash",    group: "Text",    opts: false, branch: false, value: true,  hint: "Numeric entry." },
  { id: "radio",    labelKey: "questions.useQuestions.radioButtons", get label() { return i18nT("questions.useQuestions.radioButtons"); },   icon: "radio",   group: "Choice",  opts: true,  branch: true,  value: true,  hint: "Pick one — all options visible." },
  { id: "select",   labelKey: "questions.useQuestions.dropdown", get label() { return i18nT("questions.useQuestions.dropdown"); },        icon: "caret",   group: "Choice",  opts: true,  branch: true,  value: true,  hint: "Pick one from a dropdown." },
  { id: "yesno",    labelKey: "questions.useQuestions.toggle", get label() { return i18nT("questions.useQuestions.toggle", "Toggle"); },        icon: "toggle",  group: "Choice",  opts: false, branch: true,  value: true,  hint: "On/off switch — a single yes-or-no state." },
  { id: "checks",   labelKey: "questions.useQuestions.checkboxGroup", get label() { return i18nT("questions.useQuestions.checkboxGroup"); },  icon: "checks",  group: "Choice",  opts: true,  branch: false, value: true,  hint: "Pick any number of options." },
  // RETIRED as a choice (2026-07-31): Dropdown + "allow several" replaces it.
  // Kept in the catalog because stored questions still carry `type: "multi"` and
  // must render with the right name until they are re-saved (the editor migrates
  // one to `select` on open — see normalizeQuestion).
  { id: "multi",    labelKey: "questions.useQuestions.multiSelect", get label() { return i18nT("questions.useQuestions.multiSelect"); },    icon: "multi",   group: "Choice",  opts: true,  branch: false, value: true,  hint: "Pick several from a dropdown." },
  { id: "datetime", labelKey: "questions.useQuestions.dateTime", get label() { return i18nT("questions.useQuestions.dateTime"); },     icon: "calendar",group: "Special", opts: false, branch: false, value: true,  hint: "Date/time picker." },
  { id: "file",     labelKey: "questions.useQuestions.fileUpload", get label() { return i18nT("questions.useQuestions.fileUpload"); },     icon: "upload",  group: "Special", opts: false, branch: false, value: true,  hint: "Respondent uploads a file." },
  { id: "stepper",  labelKey: "questions.useQuestions.stepper", get label() { return i18nT("questions.useQuestions.stepper", "Stepper"); },     icon: "hash",    group: "Special", opts: false, branch: false, value: true,  hint: "Numeric −/+ picker; can hold several named counts." },
] as const;
export const QTYPE = (id: string) => QFIELD_TYPES.find((t) => t.id === id) ?? QFIELD_TYPES[2];
/** The types an author can pick today — the catalog minus the retired ones. */
export const Q_PICKER_TYPES = QFIELD_TYPES.filter((t) => t.id !== "multi");

let _optSeq = 0;
export const mkOpt = (label: string) => ({ id: `o_${Date.now().toString(36)}${(_optSeq++).toString(36)}`, label });

export interface Question {
  id: string; label: string; type: string; help: string; required: boolean;
  status: "approved" | "draft"; owner: string; updated: string;
  options: { id: string; label: string }[];
  /**
   * Answer rules for short/long text fields (validation preset or custom
   * regex, per-language error message, read-only, hidden-in-booking,
   * min/max length). Optional and absent on every other field type.
   *
   * ⚠️ NOT yet persisted: `QuestionDto` in the kit-owned api-contracts has no
   * field for it, so it survives in-session but is dropped on reload.
   * REQ-108 asks for the contract fields.
   */
  textRules?: QTextRules;
  placeholder?: string;
  defaultValue?: string;
  hiddenInBooking?: boolean;
  yesno?: QYesNoConfig;
  select?: QSelectConfig;
  file?: QFileConfig;
  stepper?: QStepperConfig;
  info?: QInfoConfig;
}
export interface GroupItem { qid: string; required?: boolean; rules?: Record<string, string>; }
export interface QuestionGroup {
  id: string; name: string; description: string; status: "approved" | "draft";
  owner: string; updated: string; items: GroupItem[];
}

function toQuestion(q: Record<string, any>): Question {
  return {
    id: q.id, label: q.label ?? q.question ?? "", type: q.type ?? q.fieldType ?? "short",
    help: q.help ?? q.helpText ?? "", required: q.required ?? false,
    // Read through under either name so the screen lights up the moment the
    // contract ships the field (REQ-108) with no further CD change.
    textRules: q.textRules ?? q.text_rules ?? undefined,
    // Same for the per-type editor config — read back if the server ever
    // returns it, absent otherwise.
    ...(q.placeholder !== undefined ? { placeholder: q.placeholder } : {}),
    ...(q.defaultValue !== undefined ? { defaultValue: q.defaultValue } : {}),
    ...(q.hiddenInBooking !== undefined ? { hiddenInBooking: q.hiddenInBooking } : {}),
    ...(q.yesno !== undefined ? { yesno: q.yesno } : {}),
    ...(q.select !== undefined ? { select: q.select } : {}),
    ...(q.file !== undefined ? { file: q.file } : {}),
    ...(q.stepper !== undefined ? { stepper: q.stepper } : {}),
    ...(q.info !== undefined ? { info: q.info } : {}),
    status: q.status === "approved" ? "approved" : "draft",
    owner: q.owner ?? q.createdBy ?? "—", updated: q.updatedAt ?? q.updated_at ?? "—",
    options: (q.options ?? []).map((o: any) => typeof o === "string" ? { id: o, label: o } : { id: o.id ?? o.value ?? o.label, label: o.label ?? o.value }),
  };
}
function toGroup(g: Record<string, any>): QuestionGroup {
  return {
    id: g.id, name: g.name ?? "", description: g.description ?? "",
    status: g.status === "approved" ? "approved" : "draft",
    owner: g.owner ?? g.createdBy ?? "—", updated: g.updatedAt ?? g.updated_at ?? "—",
    items: (g.items ?? g.questions ?? []).map((it: any) => ({
      qid: it.qid ?? it.questionId ?? it.id,
      required: it.required ?? undefined,
      rules: it.rules ?? undefined,
    })),
  };
}

let _qCache: Question[] | null = null;
let _gCache: QuestionGroup[] | null = null;
let _loadError: string | null = null;
const _listeners = new Set<() => void>();
const notify = () => _listeners.forEach((fn) => fn());
onTenantSwitching(() => { _qCache = null; _gCache = null; _loadError = null; notify(); });
onTenantChange(() => { void loadAll(); });

/* ── Seed library (offline fallback / first-run demo data) ─────────────────
 * Two ready-made groups — food allergies & accessibility — with their
 * questions. Used when the contract returns an empty library so the screens
 * are never blank; a non-empty API response replaces it. */
async function loadAll() {
  const [qr, gr] = await Promise.all([listQuestions({ limit: 200 }), listQuestionGroups({ limit: 100 })])
    .catch((e) => { _loadError = e instanceof Error ? e.message : String(e); notify(); return [null, null] as const; });
  if (!qr || !gr) return;
  _loadError = null;
  const qArr = ((qr as any).questions ?? (qr as any).data ?? []).map(toQuestion);
  const gArr = ((gr as any).groups ?? (gr as any).data ?? []).map(toGroup);
  // Preserve anything created locally before hydration; render empty on a
  // genuinely-empty tenant. A FAILED read is NOT empty — it sets _loadError and
  // the screens render a visible error + retry (addendum item 31).
  const qLocal = (_qCache ?? []).filter((q) => !qArr.some((x: Question) => x.id === q.id));
  const gLocal = (_gCache ?? []).filter((g) => !gArr.some((x: QuestionGroup) => x.id === g.id));
  _qCache = qArr.length ? [...qLocal, ...qArr] : (_qCache ?? []);
  _gCache = gArr.length ? [...gLocal, ...gArr] : (_gCache ?? []);
  notify();
}

export function useQuestions() {
  const [, setV] = useState(0);

  useEffect(() => {
    const fn = () => setV((v) => v + 1);
    _listeners.add(fn);
    if (!_qCache) loadAll();
    return () => { _listeners.delete(fn); };
  }, []);

  let _seq = 0;
  const tmpId = (p: string) => `${p}_new_${Date.now().toString(36)}${(_seq++).toString(36)}`;

  return {
    questions: () => _qCache ?? [],
    loadError: () => _loadError,
    retryLoad: () => { _loadError = null; notify(); void loadAll(); },
    groups: () => _gCache ?? [],
    loading: _qCache === null,
    getQ: (id: string) => _qCache?.find((q) => q.id === id) ?? null,
    getGroup: (id: string) => _gCache?.find((g) => g.id === id) ?? null,
    usageOf: (qid: string) => (_gCache ?? []).filter((g) => g.items.some((it) => it.qid === qid)).length,
    newQuestion: (): Question => ({ id: tmpId("q"), label: "", type: "short", help: "", required: false, status: "draft", owner: "You", updated: "Just now", options: [] }),
    newGroup: (): QuestionGroup => ({ id: tmpId("g"), name: "", description: "", status: "draft", owner: "You", updated: "Just now", items: [] }),
    newOption: (label: string) => mkOpt(label),
    upsertQ: async (q: Question) => {
      const isNew = !_qCache?.some((x) => x.id === q.id);
      const prevQ = _qCache?.find((x) => x.id === q.id) ?? null;
      logUpsert("question", isNew ? null : (prevQ as unknown as Record<string, unknown>), q as unknown as Record<string, unknown>, { name: q.label });
      const opts = q.options.filter((o) => o.label.trim()).map((o) => ({ id: o.id, label: o.label }));
      // `textRules` is sent optimistically: the contract has no field for it yet
      // (REQ-108), so today the mock drops it and the rules do not survive a
      // reload. Sending it means the day the field lands, persistence works with
      // no CD change — and it is never sent as `{}`, so a question without rules
      // stays byte-identical to before.
      const rules = q.textRules && Object.keys(q.textRules).length ? { textRules: q.textRules } : {};
      // Same optimistic treatment for the per-type config the editor writes
      // (placeholder/prefill, hidden, toggle, dropdown, file, stepper, info).
      // None of it is in `QuestionDto` yet either — one REQ-108 covers the lot.
      // Each key is sent ONLY when present, so a plain question's payload is
      // byte-identical to before.
      const extra: Record<string, unknown> = {};
      for (const k of ["placeholder", "defaultValue", "hiddenInBooking", "yesno", "select", "file", "stepper", "info"] as const) {
        const v = q[k];
        if (v !== undefined) extra[k] = v;
      }
      const input = { label: q.label, type: q.type, help: q.help, required: q.required, status: q.status, options: opts, ...rules, ...extra };
      if (isNew) {
        const res = await createQuestion(input as any);
        _qCache = [...(_qCache ?? []), toQuestion((res as any).question ?? res)]; notify();
      } else {
        const res = await patchQuestion(q.id, input as any);
        const saved = toQuestion((res as any).question ?? res);
        _qCache = (_qCache ?? []).map((x) => x.id === q.id ? saved : x); notify();
      }
    },
    upsertGroup: async (g: QuestionGroup) => {
      const isNew = !_gCache?.some((x) => x.id === g.id);
      const prevG = _gCache?.find((x) => x.id === g.id) ?? null;
      logUpsert("questionGroup", isNew ? null : (prevG as unknown as Record<string, unknown>), g as unknown as Record<string, unknown>, { name: g.name });
      const input = { name: g.name, description: g.description, status: g.status, items: g.items };
      if (isNew) {
        const res = await createQuestionGroup(input as any);
        _gCache = [...(_gCache ?? []), toGroup((res as any).group ?? res)]; notify();
      } else {
        const res = await patchQuestionGroup(g.id, input as any);
        const saved = toGroup((res as any).group ?? res);
        _gCache = (_gCache ?? []).map((x) => x.id === g.id ? saved : x); notify();
      }
    },
    removeQ: async (id: string) => {
      const prev = (_qCache ?? []).find((x) => x.id === id);
      if (prev) logDelete("question", prev as unknown as Record<string, unknown>, { name: prev.label });
      await deleteQuestion(id);
      _qCache = (_qCache ?? []).filter((x) => x.id !== id); notify();
    },
    removeGroup: async (id: string) => {
      const prev = (_gCache ?? []).find((x) => x.id === id);
      if (prev) logDelete("questionGroup", prev as unknown as Record<string, unknown>, { name: prev.name });
      await deleteQuestionGroup(id);
      _gCache = (_gCache ?? []).filter((x) => x.id !== id); notify();
    },
    duplicateQ: async (id: string): Promise<Question> => {
      const src = _qCache?.find((x) => x.id === id);
      // Real typed payload (drop-33 docket §2): the duplicate facade takes a
      // QuestionDto — build it explicitly from the UI shape. `type` narrows from
      // the UI's plain string to the contract enum (values come from the QTYPE
      // catalog, which mirrors it); timestamps are required by the DTO but
      // unread by the facade.
      const nowIso = new Date().toISOString();
      if (!src) throw new Error(`question not found: ${id}`);
      const dto: QuestionDto = {
        id: src.id, label: src.label, type: src.type as QuestionDto["type"],
        help: src.help || undefined, required: src.required,
        options: src.options.map((o) => ({ id: o.id, label: o.label })),
        status: src.status, owner: src.owner || undefined,
        updatedAt: nowIso, createdAt: nowIso,
      };
      const res = await duplicateQuestion(dto);
      const copy = toQuestion((res as any).question ?? res);
      _qCache = [...(_qCache ?? []), copy]; notify();
      return copy;
    },
    duplicateGroup: async (id: string): Promise<QuestionGroup> => {
      const srcG = _gCache?.find((x) => x.id === id);
      const nowIso = new Date().toISOString();
      if (!srcG) throw new Error(`question group not found: ${id}`);
      const gDto: QuestionGroupDto = {
        id: srcG.id, name: srcG.name, description: srcG.description || undefined,
        status: srcG.status,
        items: srcG.items.map((it) => ({ qid: it.qid, required: it.required, rules: it.rules })),
        usedBy: 0, updatedAt: nowIso, createdAt: nowIso,
      };
      const res = await duplicateQuestionGroup(gDto);
      const copy = toGroup((res as any).group ?? res);
      _gCache = [...(_gCache ?? []), copy]; notify();
      return copy;
    },
    reload: () => { _qCache = null; _gCache = null; loadAll(); },
  };
}
